import {
  IPlanSummaryData,
  PlanUnit,
  BookingTypeEnum,
  IIssuingDetail,
  IIssuingLog,
  IssuingRecordActionEnum,
  IUserTokenData,
  IOperatedTicketDetail,
  ITicketDetail,
  IssuedStatusEnum,
  CommonLocale,
  TicketSetting,
  IssuableDate,
  IssuingRule,
  SettingApplyingRule,
  adaptRequestWithLocale,
  PlanSetting,
  PlanSettingPrintSpec,
  PlanSettingUISetting,
  I18nText,
  MogiriSettingPlan,
  MogiriSettingTicket,
} from './types';
import request from '@/utils/request';
import { sleep, formatDate } from '@/utils/index';
import {
  bookingDetailResponseWithCache,
  IGetBookingDetailResponse,
} from './booking';
import { getUserToken } from '@/utils/localstorage';

// request & response
export interface IListPlansRequest {
  locale?: CommonLocale;
}

export interface IListPlansResponse {
  plans: IPlanSummaryData[];
  locale?: CommonLocale;
}

export interface IListPlanSettingsRequest {}

export interface IListPlanSettingsResponse {
  plans: PlanSetting[];
}

export interface IGetPlanSettingRequest {
  planId: string;
}

export interface IPutPlanSettingRequest {
  planSettingId?: string;
  planId: string;
  issuingRules: IssuingRule[];
  issuableFrom: IssuableDate[];
  issuableTo: IssuableDate[];
  applyingRule: SettingApplyingRule | null;
  printSpecs: PlanSettingPrintSpec[];
  uiSetting: PlanSettingUISetting;
  mogiriSetting: MogiriSettingPlan;
  isInactive: boolean;
  version?: string;
}

export interface IPutPlanSettingResponse {
  planSettingId: string;
}

export interface IListTicketSettingsRequest {
  planId?: string;
  unitId?: string;
}

export interface IListTicketSettingsResponse {
  tickets: TicketSetting[];
}

export interface IPutTicketSettingRequest {
  planId: string;
  unitId: string;
  ticketId?: string;
  issuingRules: IssuingRule[];
  issuableFrom: IssuableDate[];
  issuableTo: IssuableDate[];
  title: string;
  titlesForTraveler?: I18nText[];
  applyingRule: SettingApplyingRule | null;
  isInactive: boolean;
  mogiriSetting?: MogiriSettingTicket;
  version?: string;
  priority: number | null;
}

export interface IPutTicketSettingResponse {
  ticketId: string;
}

export interface IIssueRequest {
  bookingId: string;
  details: IIssuingDetail[];
  note?: string;
  systemSource?: string;
  bookingRecordVersion: string;
}

export interface IRevokeRequest {
  bookingId: string;
  issuingRecordId: string;
  note?: string;
  systemSource?: string;
  bookingRecordVersion: string;
}

export interface IRefundRequest {
  bookingId: string;
  details: IIssuingDetail[];
  note?: string;
  systemSource?: string;
  bookingRecordVersion: string;
}

export interface IRechargeRequest {
  bookingId: string;
  issuingRecordId: string;
  note?: string;
  systemSource?: string;
  bookingRecordVersion: string;
}

export interface IIssueResponse {
  issuingRecordId: string;
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';
const useMockWithCache = process.env.VUE_APP_API_USE_MOCK === 'true';

const defaultPlanSummaryData: IPlanSummaryData = {
  activityId: 'activityId',
  activityTitle: 'activityTitle',
  planId: '',
  planTitle: 'planTitle',
  units: [
    { id: 'adult', title: 'Adult' },
    { id: 'child', title: 'Child' },
  ],
  bookingType: BookingTypeEnum.EACH_TIME,
};

const mockPlanSummaryDataById = (id: number, req?: IListPlansRequest) => {
  const result = Object.assign({}, defaultPlanSummaryData);

  result.activityId += id % 2;
  result.activityTitle += id % 2;
  result.planId = `${result.activityId}-${id % 2}`;
  result.planTitle += id % 2;

  return result;
};

const mockPlanSummaryDataSize = 2;

const mockPlanSummaryDataList = (
  req?: IListPlansRequest,
  totalCount: number = mockPlanSummaryDataSize
) => {
  const plans: IPlanSummaryData[] = [];
  for (let i = 0; i < totalCount; i++) {
    plans.push(mockPlanSummaryDataById(i, req));
  }
  return plans;
};

const nextLogId = (detailResponse: IGetBookingDetailResponse) =>
  `${detailResponse.booking.supplierName}:${
    detailResponse.issuingLogs && detailResponse.issuingLogs.length > 0
      ? parseInt(
          detailResponse.issuingLogs[0].id.split(
            `${detailResponse.booking.supplierName}:`
          )[1],
          10
        ) + 1
      : 1
  }`;

const updateTicketsStatus = (detailResponse: IGetBookingDetailResponse) => {
  let totalCount = 0;
  let issuedCount = 0;
  detailResponse.tickets.forEach(t => {
    t.issuedTickets.forEach(o => {
      totalCount += o.amount;
      issuedCount += o.amount;
    });
    t.unissuableTickets.forEach(o => {
      totalCount += o.amount;
    });
    t.issuableTickets.forEach(o => {
      totalCount += o.amount;
    });
  });
  if (totalCount === issuedCount) {
    detailResponse.issuedStatus = IssuedStatusEnum.ISSUED;
  } else if (issuedCount === 0) {
    detailResponse.issuedStatus = IssuedStatusEnum.NOT_ISSUED;
  } else {
    detailResponse.issuedStatus = IssuedStatusEnum.PARTIAL;
  }
};

const mockIssueTicketsWithCache = (req: IIssueRequest) => {
  const detailResponse = bookingDetailResponseWithCache[req.bookingId];
  detailResponse.issuingLogs = detailResponse.issuingLogs || [];
  // update issuing log
  const userToken = JSON.parse(getUserToken() as string) as IUserTokenData;
  const logId = nextLogId(detailResponse);
  const log: IIssuingLog = {
    id: logId,
    bookingId: req.bookingId,
    operatedTickets: req.details.map(
      d =>
        ({
          id: d.ticketId,
          title: detailResponse.tickets.find(t => t.ticketId === d.ticketId)!
            .title,
          amount: d.amount,
        } as IOperatedTicketDetail)
    ),
    action: IssuingRecordActionEnum.ISSUE as IssuingRecordActionEnum,
    operatedAt: new Date().toISOString(),
    accountId: userToken.accountId,
    accountName: userToken.username,
  };
  detailResponse.issuingLogs!.unshift(log);
  // update tickets
  detailResponse.tickets.forEach(t => {
    const found = req.details.find(d => d.ticketId === t.ticketId);
    if (found) {
      let remain = found.amount;
      // update issuable & issued tickets
      while (remain > 0 && t.issuableTickets.length > 0) {
        const issuedTicket = t.issuedTickets.find(
          issued =>
            issued.issuableFrom === t.issuableTickets[0].issuableFrom &&
            issued.issuableTo === t.issuableTickets[0].issuableTo
        );

        if (remain >= t.issuableTickets[0].amount) {
          remain -= t.issuableTickets[0].amount;
          const removed = t.issuableTickets.shift() as ITicketDetail;
          if (issuedTicket) {
            issuedTicket.amount += removed.amount;
          } else {
            t.issuedTickets.push({
              issuableFrom: removed.issuableFrom,
              issuableTo: removed.issuableTo,
              amount: removed.amount,
            } as ITicketDetail);
          }
        } else {
          t.issuableTickets[0].amount -= remain;
          if (issuedTicket) {
            issuedTicket.amount += remain;
          } else {
            t.issuedTickets.push({
              issuableFrom: t.issuableTickets[0].issuableFrom,
              issuableTo: t.issuableTickets[0].issuableTo,
              amount: remain,
            } as ITicketDetail);
          }
          remain = 0;
        }
      }
    }
  });
  // update ticket status
  updateTicketsStatus(detailResponse);
  return { issuingRecordId: logId } as IIssueResponse;
};

const mockRefundTicketsWithCache = (req: IRefundRequest) => {
  const detailResponse = bookingDetailResponseWithCache[req.bookingId];
  detailResponse.issuingLogs = detailResponse.issuingLogs || [];
  // update issuing log
  const userToken = JSON.parse(getUserToken() as string) as IUserTokenData;
  const logId = nextLogId(detailResponse);
  const log: IIssuingLog = {
    id: logId,
    bookingId: req.bookingId,
    operatedTickets: req.details.map(
      d =>
        ({
          id: d.ticketId,
          amount: d.amount,
        } as IOperatedTicketDetail)
    ),
    action: IssuingRecordActionEnum.REFUND as IssuingRecordActionEnum,
    operatedAt: new Date().toISOString(),
    accountId: userToken.accountId,
    accountName: userToken.username,
  };
  detailResponse.issuingLogs!.unshift(log);
  // update tickets
  detailResponse.tickets.forEach(t => {
    const found = req.details.find(d => d.ticketId === t.ticketId);
    if (found) {
      let remain = found.amount;
      // update issuable & issued tickets
      while (remain > 0 && t.issuableTickets.length > 0) {
        const issuedTicket = t.issuedTickets.find(
          issued =>
            issued.issuableFrom === t.issuableTickets[0].issuableFrom &&
            issued.issuableTo === t.issuableTickets[0].issuableTo
        );

        if (remain >= t.issuableTickets[0].amount) {
          remain -= t.issuableTickets[0].amount;
          const removed = t.issuableTickets.shift() as ITicketDetail;
          if (issuedTicket) {
            issuedTicket.amount += removed.amount;
          } else {
            t.issuedTickets.push({
              issuableFrom: removed.issuableFrom,
              issuableTo: removed.issuableTo,
              amount: removed.amount,
            } as ITicketDetail);
          }
        } else {
          t.issuableTickets[0].amount -= remain;
          if (issuedTicket) {
            issuedTicket.amount += remain;
          } else {
            t.issuedTickets.push({
              issuableFrom: t.issuableTickets[0].issuableFrom,
              issuableTo: t.issuableTickets[0].issuableTo,
              amount: remain,
            } as ITicketDetail);
          }
          remain = 0;
        }
      }
    }
  });
  // update ticket status
  updateTicketsStatus(detailResponse);
  return { issuingRecordId: logId } as IIssueResponse;
};

const mockRevokeTicketsWithCache = (req: IRevokeRequest) => {
  const detailResponse = bookingDetailResponseWithCache[req.bookingId];
  detailResponse.issuingLogs = detailResponse.issuingLogs || [];
  const operatedTickets =
    (detailResponse.issuingLogs.find(
      l => l.id === req.issuingRecordId
    ) as IIssuingLog).operatedTickets || [];
  // update issuing log
  const userToken = JSON.parse(getUserToken() as string) as IUserTokenData;
  const logId = nextLogId(detailResponse);
  const log: IIssuingLog = {
    id: logId,
    bookingId: req.bookingId,
    operatedTickets: operatedTickets,
    action: IssuingRecordActionEnum.REVOKE as IssuingRecordActionEnum,
    operatedAt: new Date().toISOString(),
    accountId: userToken.accountId,
    accountName: userToken.username,
    referenceId: req.issuingRecordId,
  };
  detailResponse.issuingLogs!.unshift(log);
  // update tickets
  detailResponse.tickets.forEach(t => {
    const found = operatedTickets.find(d => d.id === t.ticketId);
    if (found) {
      let remain = found.amount;
      // update issued & issuable tickets
      while (remain > 0 && t.issuedTickets.length > 0) {
        const issuableTicket = t.issuableTickets.find(
          issuable =>
            issuable.issuableFrom === t.issuedTickets[0].issuableFrom &&
            issuable.issuableTo === t.issuedTickets[0].issuableTo
        );

        if (remain >= t.issuedTickets[0].amount) {
          remain -= t.issuedTickets[0].amount;
          const removed = t.issuedTickets.shift() as ITicketDetail;
          if (issuableTicket) {
            issuableTicket.amount += removed.amount;
          } else {
            t.issuableTickets.push({
              issuableFrom: removed.issuableFrom,
              issuableTo: removed.issuableTo,
              amount: removed.amount,
            } as ITicketDetail);
          }
        } else {
          t.issuedTickets[0].amount -= remain;
          if (issuableTicket) {
            issuableTicket.amount += remain;
          } else {
            t.issuableTickets.push({
              issuableFrom: t.issuedTickets[0].issuableFrom,
              issuableTo: t.issuedTickets[0].issuableTo,
              amount: remain,
            } as ITicketDetail);
          }
          remain = 0;
        }
      }
    }
  });
  // update ticket status
  updateTicketsStatus(detailResponse);

  return { issuingRecordId: logId } as IIssueResponse;
};

const mockRechargeTicketsWithCache = (req: IRechargeRequest) => {
  const detailResponse = bookingDetailResponseWithCache[req.bookingId];
  detailResponse.issuingLogs = detailResponse.issuingLogs || [];
  const operatedTickets =
    (detailResponse.issuingLogs.find(
      l => l.id === req.issuingRecordId
    ) as IIssuingLog).operatedTickets || [];
  // update issuing log
  const userToken = JSON.parse(getUserToken() as string) as IUserTokenData;
  const logId = nextLogId(detailResponse);
  const log: IIssuingLog = {
    id: logId,
    bookingId: req.bookingId,
    operatedTickets: operatedTickets,
    action: IssuingRecordActionEnum.RECHARGE as IssuingRecordActionEnum,
    operatedAt: new Date().toISOString(),
    accountId: userToken.accountId,
    accountName: userToken.username,
    referenceId: req.issuingRecordId,
  };
  detailResponse.issuingLogs!.unshift(log);
  // update tickets
  detailResponse.tickets.forEach(t => {
    const found = operatedTickets.find(d => d.id === t.ticketId);
    if (found) {
      let remain = found.amount;
      // update issued & issuable tickets
      while (remain > 0 && t.issuedTickets.length > 0) {
        const issuableTicket = t.issuableTickets.find(
          issuable =>
            issuable.issuableFrom === t.issuedTickets[0].issuableFrom &&
            issuable.issuableTo === t.issuedTickets[0].issuableTo
        );

        if (remain >= t.issuedTickets[0].amount) {
          remain -= t.issuedTickets[0].amount;
          const removed = t.issuedTickets.shift() as ITicketDetail;
          if (issuableTicket) {
            issuableTicket.amount += removed.amount;
          } else {
            t.issuableTickets.push({
              issuableFrom: removed.issuableFrom,
              issuableTo: removed.issuableTo,
              amount: removed.amount,
            } as ITicketDetail);
          }
        } else {
          t.issuedTickets[0].amount -= remain;
          if (issuableTicket) {
            issuableTicket.amount += remain;
          } else {
            t.issuableTickets.push({
              issuableFrom: t.issuedTickets[0].issuableFrom,
              issuableTo: t.issuedTickets[0].issuableTo,
              amount: remain,
            } as ITicketDetail);
          }
          remain = 0;
        }
      }
    }
  });
  // update ticket status
  updateTicketsStatus(detailResponse);

  return { issuingRecordId: logId } as IIssueResponse;
};

// api
export const listPlans = async (req: IListPlansRequest) => {
  if (useMock) {
    return { plans: mockPlanSummaryDataList(req) };
  }

  const res = await request({
    url: 'v1/list-plans',
    method: 'post',
    data: adaptRequestWithLocale(req),
  });
  return res.data as IListPlansResponse;
};

export const listPlanSettings = async (req: IListPlanSettingsRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/list-plan-settings',
    method: 'post',
    data: req,
  });
  return res.data as IListPlanSettingsResponse;
};

export const getPlanSetting = async (req: IGetPlanSettingRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/get-plan-setting',
    method: 'post',
    data: req,
  });
  return res.data as PlanSetting;
};

export const putPlanSetting = async (req: IPutPlanSettingRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/put-plan-setting',
    method: 'post',
    data: req,
  });
  return res.data as IPutPlanSettingResponse;
};

export const listTicketSettings = async (req: IListTicketSettingsRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/list-ticket-settings',
    method: 'post',
    data: req,
  });
  return res.data as IListTicketSettingsResponse;
};

export const putTicketSetting = async (req: IPutTicketSettingRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/put-ticket-setting',
    method: 'post',
    data: req,
  });
  return res.data as IPutTicketSettingResponse;
};

export const issueTickets = async (req: IIssueRequest) => {
  if (useMock) {
    await sleep(1000);
    if (useMockWithCache) {
      return mockIssueTicketsWithCache(req);
    }
    return { issuingRecordId: 'dummyIssuingRecordId' } as IIssueResponse;
  }

  const res = await request({
    url: 'v1/issue-tickets',
    method: 'post',
    data: req,
  });
  return res.data as IIssueResponse;
};

export const refundTickets = async (req: IRefundRequest) => {
  if (useMock) {
    await sleep(1000);
    if (useMockWithCache) {
      return mockRefundTicketsWithCache(req);
    }
    return { issuingRecordId: 'dummyIssuingRecordId' } as IIssueResponse;
  }

  const res = await request({
    url: 'v1/refund-tickets',
    method: 'post',
    data: req,
  });
  return res.data as IIssueResponse;
};

export const revokeIssuedTickets = async (req: IRevokeRequest) => {
  if (useMock) {
    await sleep(1000);
    if (useMockWithCache) {
      return mockRevokeTicketsWithCache(req);
    }
    return { issuingRecordId: 'dummyIssuingRecordId' } as IIssueResponse;
  }

  const res = await request({
    url: 'v1/revoke-issued-tickets',
    method: 'post',
    data: req,
  });
  return res.data as IIssueResponse;
};

export const rechargeTickets = async (req: IRechargeRequest) => {
  if (useMock) {
    await sleep(1000);
    if (useMockWithCache) {
      return mockRechargeTicketsWithCache(req);
    }
    return { issuingRecordId: 'dummyIssuingRecordId' } as IIssueResponse;
  }

  const res = await request({
    url: 'v1/recharge-tickets',
    method: 'post',
    data: req,
  });
  return res.data as IIssueResponse;
};
